import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { Icon, Button, Avatar, Badge, Field } from '@/components/ui';
import { useAccounts } from './hooks/useAccounts';
import { AccountsTab } from './lib/deriveRows';
import { AccountView } from './lib/mapAccount';
import { AccountsCreateFab } from './AccountsCreateFab';
import { AccountActionsModal } from './AccountActionsModal';
import { AccountsExportButton } from './AccountsExportButton';

const CARD =
  'tw-text-left tw-border tw-border-solid tw-border-ds-hairline tw-rounded-ds-m tw-bg-white tw-p-[14px] tw-m-0 tw-flex tw-flex-col tw-gap-[10px] tw-shadow-ds-e1 tw-font-poppins tw-w-full';

// The card's contents, shared by the active (button) and inactive (div) shells.
const cardBody = (c: AccountView) => (
  <>
    <div className="tw-flex tw-items-center tw-gap-[10px]">
      <span
        title={c.active ? 'Active' : 'Inactive'}
        className={clsx(
          'tw-w-[8px] tw-h-[8px] tw-rounded-full tw-flex-none',
          c.active ? 'tw-bg-ds-success' : 'tw-bg-ds-ink-mute'
        )}
      />
      <span
        className={clsx(
          'tw-text-[15px] tw-font-semibold tw-flex-1 tw-min-w-0 tw-leading-[1.3] tw-truncate',
          c.active ? 'tw-text-ds-ink' : 'tw-text-ds-ink-mute'
        )}
      >
        {c.name}
      </span>
      {c.active ? (
        <Icon name="chevron-right" size={18} color="var(--ink-mute)" />
      ) : (
        <Badge>Inactive</Badge>
      )}
    </div>
    <div className="tw-flex tw-items-center tw-gap-[9px] tw-pl-[18px] tw-min-w-0">
      <Avatar name={c.managerName} src={c.managerAvatarUrl} size={24} />
      <span className="tw-text-[13px] tw-text-ds-storm tw-truncate">
        {c.managerName}
      </span>
    </div>
    <div className="tw-flex tw-items-center tw-gap-[7px] tw-pl-[18px] tw-min-w-0">
      <Icon name="mail" size={14} color="var(--ink-mute)" />
      <span className="tw-text-[12.5px] tw-text-ds-ink-soft tw-truncate">
        {c.email}
      </span>
    </div>
  </>
);

const TabBtn = ({
  id,
  label,
  n,
  active,
  onSelect,
}: {
  id: AccountsTab;
  label: string;
  n: number;
  active: boolean;
  onSelect: (id: AccountsTab) => void;
}) => (
  <button
    onClick={() => onSelect(id)}
    className={clsx(
      'tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-[7px] tw-h-[40px] tw-rounded-full tw-border-0 tw-cursor-pointer tw-font-poppins tw-text-[13.5px] tw-transition-colors',
      active
        ? 'tw-bg-ds-primary tw-text-white tw-font-semibold'
        : 'tw-bg-transparent tw-text-ds-ink-soft tw-font-medium'
    )}
  >
    {label}
    <span
      className={clsx(
        'tw-text-[11px] tw-font-semibold tw-rounded-full tw-px-[7px] tw-py-px',
        active
          ? 'tw-bg-white/[.22] tw-text-white'
          : 'tw-bg-ds-neutral-bg tw-text-ds-ink-mute'
      )}
    >
      {n}
    </span>
  </button>
);

export const AccountsMobileList = () => {
  const history = useHistory();
  const [tab, setTab] = useState<AccountsTab>('companies');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  // The account whose actions modal is open, or null.
  const [managing, setManaging] = useState<AccountView | null>(null);

  const {
    rows,
    isLoading,
    isError,
    companiesCount,
    projectsCount,
    activeCompaniesCount,
    activeProjectsCount,
  } = useAccounts({ tab, search, showInactive, sortAsc: true });

  const totalForTab = tab === 'companies' ? companiesCount : projectsCount;
  // Inactive accounts are a dead end: GET /api/project/detail/:id answers 400
  // "Inactive Project", the workspace never swaps in, and the shell is left showing
  // whichever account was open before. Block the navigation at the source instead.
  const openAccount = (r: AccountView) => {
    if (!r.active) return;
    history.push(`/projects/${r.id}`);
  };

  return (
    <div className="tw-h-full tw-flex tw-flex-col tw-min-h-0 tw-bg-ds-table-header">
      {/* sticky controls */}
      <div className="tw-flex-none tw-bg-white tw-border-b tw-border-ds-hairline tw-px-[16px] tw-pt-[14px] tw-pb-[12px]">
        <h1 className="tw-font-poppins tw-text-[22px] tw-font-semibold tw-tracking-[-0.01em] tw-text-ds-ink tw-m-0 tw-mb-[2px]">
          Accounts
        </h1>
        <p className="app-sm tw-m-0 tw-mb-[12px]">
          {activeCompaniesCount} active companies · {activeProjectsCount} active
          internal projects
        </p>
        <div className="tw-flex tw-gap-[4px] tw-bg-ds-table-header tw-border tw-border-ds-hairline tw-rounded-full tw-p-[3px] tw-mb-[12px]">
          <TabBtn
            id="companies"
            label="Companies"
            n={companiesCount}
            active={tab === 'companies'}
            onSelect={setTab}
          />
          <TabBtn
            id="projects"
            label="Internal Projects"
            n={projectsCount}
            active={tab === 'projects'}
            onSelect={setTab}
          />
        </div>
        <Field
          icon="search"
          placeholder={
            tab === 'companies'
              ? 'Search companies…'
              : 'Search internal projects…'
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
        />
        {/* Filters and Export share a row: the create FAB already owns the bottom-right
            corner, so a second floating control there would collide with it. */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-[12px] tw-mt-[12px]">
          <label className="tw-inline-flex tw-items-center tw-gap-[8px] tw-cursor-pointer tw-select-none">
            <input
              type="checkbox"
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span
              className={clsx(
                'tw-w-[36px] tw-h-[20px] tw-rounded-full tw-relative tw-transition-colors tw-flex-none',
                showInactive ? 'tw-bg-ds-primary' : 'tw-bg-ds-field-disabled'
              )}
            >
              <span
                className={clsx(
                  'tw-absolute tw-top-[2px] tw-w-[16px] tw-h-[16px] tw-rounded-full tw-bg-white tw-shadow-ds-e1 tw-transition-all',
                  showInactive ? 'tw-left-[18px]' : 'tw-left-[2px]'
                )}
              />
            </span>
            <span className="tw-font-poppins tw-text-[13px] tw-text-ds-ink-soft">
              Show inactive
            </span>
          </label>
          <AccountsExportButton rows={rows} tab={tab} />
        </div>
      </div>

      {/* scrollable cards */}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-[16px] tw-pt-[12px] tw-pb-[24px]">
        {isLoading && (
          <div className="tw-py-[56px] tw-text-center tw-font-poppins tw-text-[14px] tw-text-ds-ink-mute">
            Loading accounts…
          </div>
        )}

        {!isLoading && isError && (
          <div className="tw-py-[56px] tw-flex tw-flex-col tw-items-center tw-gap-[12px] tw-text-ds-ink-mute">
            <Icon name="x" size={28} color="var(--danger)" />
            <span className="tw-font-poppins tw-text-[14px] tw-text-center tw-text-ds-storm">
              Couldn’t load accounts. Check your connection and try again.
            </span>
          </div>
        )}

        {!isLoading && !isError && (
          <div className="tw-flex tw-flex-col tw-gap-[10px]">
            {/* An inactive card is a div, not a disabled button: a nested button inside a
                disabled button never receives clicks, and the Manage button lives inside. */}
            {rows.map((c) =>
              c.active ? (
                <button
                  key={c.id}
                  onClick={() => openAccount(c)}
                  className={clsx(CARD, 'tw-cursor-pointer')}
                >
                  {cardBody(c)}
                </button>
              ) : (
                <div key={c.id} aria-disabled className={CARD}>
                  {cardBody(c)}
                  {c.canManage && (
                    <button
                      type="button"
                      aria-label={`Manage ${c.name}`}
                      onClick={() => setManaging(c)}
                      className="tw-self-start tw-ml-[18px] tw-inline-flex tw-items-center tw-gap-[6px] tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer tw-font-poppins tw-text-[13px] tw-font-semibold tw-text-ds-primary"
                    >
                      <Icon
                        name="pencil"
                        size={14}
                        color="var(--twilight-sky)"
                      />
                      Manage account
                    </button>
                  )}
                </div>
              )
            )}

            {rows.length === 0 && (
              <div className="tw-py-[56px] tw-flex tw-flex-col tw-items-center tw-gap-[12px] tw-text-ds-ink-mute">
                <Icon name="search-x" size={28} color="var(--ink-mute)" />
                <span className="tw-font-poppins tw-text-[14px] tw-text-center">
                  No accounts match your filters.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearch('');
                    setShowInactive(true);
                  }}
                >
                  Clear filters
                </Button>
              </div>
            )}

            {rows.length > 0 && (
              <p className="app-sm tw-text-center tw-mt-[16px]">
                Showing {rows.length} of {totalForTab}{' '}
                {tab === 'companies' ? 'companies' : 'projects'}
              </p>
            )}
          </div>
        )}
      </div>

      <AccountActionsModal
        account={managing}
        onClose={() => setManaging(null)}
      />
      <AccountsCreateFab tab={tab} />
    </div>
  );
};

export default AccountsMobileList;
